/**
 * Approval node configuration type and validation
 */

export type ApproverConfig = {
  userIds: string[];
  roleIds: string[];
};

export type ApprovalConfigType = {
  approvers: ApproverConfig;
  instructions?: string;
  timeoutEnabled: boolean;
  timeoutDuration?: number;
  timeoutUnit?: 'hours' | 'days';
  timeoutAction?: 'reject' | 'approve';
};

export const defaultApprovalConfig: ApprovalConfigType = {
  approvers: {
    userIds: [],
    roleIds: [],
  },
  instructions: '',
  timeoutEnabled: false,
  timeoutDuration: 24,
  timeoutUnit: 'hours',
  timeoutAction: 'reject',
};

export const validateApprovalConfig = (
  config: ApprovalConfigType,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // At least one approver (user or role) is required
  if (
    (!config.approvers?.userIds || config.approvers.userIds.length === 0) &&
    (!config.approvers?.roleIds || config.approvers.roleIds.length === 0)
  ) {
    errors.approvers = 'At least one approver (user or role) must be specified';
  }

  // Validate timeout configuration if enabled
  if (config.timeoutEnabled) {
    if (!config.timeoutDuration || config.timeoutDuration <= 0) {
      errors.timeoutDuration = 'Timeout duration must be greater than 0';
    }
    if (!config.timeoutUnit) {
      errors.timeoutUnit = 'Timeout unit is required';
    }
    if (!config.timeoutAction) {
      errors.timeoutAction = 'Timeout action is required';
    }
  }

  return errors;
};

/**
 * Get preview text for the node display
 */
export const getApprovalPreviewText = (config: ApprovalConfigType): string => {
  const approverCount =
    (config.approvers?.userIds?.length || 0) +
    (config.approvers?.roleIds?.length || 0);

  if (approverCount === 0) {
    return 'Configure approvers';
  }

  const parts: string[] = [];

  if (config.approvers?.userIds?.length) {
    parts.push(
      `${config.approvers.userIds.length} user${config.approvers.userIds.length > 1 ? 's' : ''}`,
    );
  }

  if (config.approvers?.roleIds?.length) {
    parts.push(
      `${config.approvers.roleIds.length} role${config.approvers.roleIds.length > 1 ? 's' : ''}`,
    );
  }

  let text = parts.join(' + ');

  if (config.timeoutEnabled && config.timeoutDuration && config.timeoutUnit) {
    text += ` • ${config.timeoutDuration}${config.timeoutUnit === 'hours' ? 'h' : 'd'} timeout`;
  }

  return text;
};
